import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { BeforeAfterProject } from '@/types/firestore';

interface BeforeAfterGalleryProps {
  projects: BeforeAfterProject[];
  autoPlayMs?: number;
}

export function BeforeAfterGallery({ projects, autoPlayMs = 6000 }: BeforeAfterGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAfter, setShowAfter] = useState(false);

  useEffect(() => {
    if (projects.length === 0) return;

    // Toggle between before/after every 2 seconds within each project
    const toggleTimer = setInterval(() => {
      setShowAfter((prev) => !prev);
    }, 2000);

    return () => clearInterval(toggleTimer);
  }, [projects.length]);

  useEffect(() => {
    if (projects.length <= 1) return;

    // Move to next project
    const projectTimer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % projects.length);
      setShowAfter(false);
    }, autoPlayMs);

    return () => clearInterval(projectTimer);
  }, [projects.length, autoPlayMs]);

  if (!projects || projects.length === 0) return null;

  const project = projects[currentIndex];

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
    setShowAfter(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % projects.length);
    setShowAfter(false);
  };

  return (
    <Card className="overflow-hidden bg-background/50 backdrop-blur-sm border-border/40">
      <div className="relative aspect-[4/3]">
        {/* Before Image */}
        <img
          src={project.before_url}
          alt={`${project.project_type} - Before`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            showAfter ? 'opacity-0' : 'opacity-100'
          }`}
        />

        {/* After Image */}
        <img
          src={project.after_url}
          alt={`${project.project_type} - After`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            showAfter ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Before/After Badge */}
        <Badge
          variant="secondary"
          className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm"
        >
          {showAfter ? 'After' : 'Before'}
        </Badge>

        {/* Project Type Badge */}
        <Badge
          variant="default"
          className="absolute top-3 right-3 bg-primary/90 backdrop-blur-sm"
        >
          {project.project_type}
        </Badge>

        {/* Navigation Arrows */}
        {projects.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/50 backdrop-blur-sm hover:bg-background/80"
              onClick={goToPrev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/50 backdrop-blur-sm hover:bg-background/80"
              onClick={goToNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/80 to-transparent" />
      </div>

      {/* Description */}
      {project.description && (
        <div className="p-3">
          <p className="text-xs text-muted-foreground text-center line-clamp-2">
            {project.description}
          </p>
        </div>
      )}

      {/* Dots indicator */}
      {projects.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-3">
          {projects.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setCurrentIndex(i);
                setShowAfter(false);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? 'w-4 bg-primary'
                  : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to project ${i + 1}`}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
